<script setup lang="ts">
import AdministrationChrome from '@voix/voix-layer/components/administration/AdministrationChrome.vue'
import AdminButton from '@voix/voix-layer/components/administration/AdministrationButton.vue'
import DropdownSelect from '@voix/components/chrome/controls/DropdownSelect.vue'
import FormSection from '@voix/voix-layer/components/administration/AdministrationFormSection.vue'
import FormField from '@voix/voix-layer/components/administration/AdministrationFormField.vue'
import SubmissionGroup from '@voix/voix-layer/components/administration/AdministrationSubmissionGroup.vue'
import GlobalField from '@voix/components/chrome/fields/GlobalField.vue'

import { computed, reactive, ref, watch } from 'vue'
import { useAdminStore } from '@voix/store/adminStore'
import { usePageStore } from '@voix/store/pageStore'
import type { Ref } from 'vue'
import type { FieldInterface } from '@voix/types'
import { useNuxtApp } from '#imports'

const emit = defineEmits(['back'])
const { $voix } = useNuxtApp()

const adminStore = useAdminStore()
const pageStore = usePageStore()

const field: Ref<string> = ref('')
const fieldOptions = computed(() => {
  const pageFields: { [key: string]: any } = $voix.options.pageFields

  // Filter out the fields that have already been used on the page
  const unusedFields = Object.keys(pageFields).filter((key) => {
    // Compare the pagefields to what is in pageStore.currentPage.fields
    return !pageStore.currentPage.fields.find((f: FieldInterface) => f.name === key)
  })

  // Build up the fieldOptions array from unusedFields
  const fieldOptions = unusedFields.map((key) => {
    return {
      key,
      value: key,
      label: pageFields[key].label,
    }
  })

  // Also add in the current field so we can display it / reselct it
  if (pageFields[field.value]) {
    fieldOptions.push({
      key: field.value,
      value: field.value,
      label: pageFields[field.value].label,
    })
  }

  return fieldOptions
})

const fieldConfig = computed(() => {
  return $voix.options.pageFields[field.value]
})

const currentId: Ref<string | null> = ref(null)

const theField = computed(() => {
  return pageStore.currentPage.fields.find((f: FieldInterface) => f.id === currentId.value)
})

watch(field, () => {
  if (currentId.value)
    pageStore.removePageField(currentId)

  const newFieldConfig = $voix.options.pageFields[field.value]
  newFieldConfig.name = field.value

  const newField = adminStore.createNewField(newFieldConfig)
  pageStore.addPageField(newField)
  currentId.value = newField.id
})

function requestSave() {
  emit('back')
}

function requestCancel() {
  if (currentId.value)
    pageStore.removePageField(currentId.value)

  emit('back')
}
</script>

<template>
  <AdministrationChrome
    title="New Page Field"
    :links="[
      {
        label: 'Back to All Page Field',
        emit: 'back',
      },
    ]"
    @back="requestCancel"
  >
    <FormSection
      title="Page Field Settings"
    >
      <FormField v-if="!theField" label="Tag Type">
        <AdministrationAlert v-if="fieldOptions.length < 1" type="info">
          <p>
            There are no more fields available to add to this page.
          </p>
        </AdministrationAlert>

        <DropdownSelect v-if="fieldOptions.length" id="field-select" v-model="field" placeholder="Select a field" :options="fieldOptions" />
      </FormField>

      <FormField v-else label="Value">
        <GlobalField
          v-if="fieldConfig"
          :field-data="theField"
          :field-configuration="fieldConfig"
          :is-page-field="true"
        />
      </FormField>
    </FormSection>

    <SubmissionGroup>
      <AdminButton type="primary" @click="requestSave">
        Done
      </AdminButton>
      <AdminButton type="secondary" @click="requestCancel">
        Cancel
      </AdminButton>
    </SubmissionGroup>
  </AdministrationChrome>
</template>
